import { Ingredient, IngredientRecipeSubgraphResult, Image, RecipeListItem, Tag } from '../api/types.js';
import {
  listIngredients, listTransformations, listTools, deleteIngredient, deleteTransformation, deleteTool,
  mergeIngredients, mergeTransformations, mergeTools, getRecipesUsingIngredient,
  getRecipesUsingTransformation, getRecipesUsingTool, getIngredientRecipeSubgraphs,
} from '../api/index.js';
import { authService } from '../auth/AuthService.js';
import { editModeService } from '../edit-mode/EditModeService.js';
import { getDefaultImageUrl } from '../recipe_renderer/imageUtils.js';
import { InlineEntityEditor, EntityData, EntityType } from '../recipe_renderer/components/InlineEntityEditor.js';
import { buildSubgraphFooterHtml, buildSubgraphIngredientsHtml, buildSubgraphPreparationHtml } from './subgraphElaboration.js';
import { MergeDialog } from './MergeDialog.js';
import { showToast } from '../ui/toast.js';
import { unitCache } from '../unit-cache.js';
import { tagChipStyle } from '../tags/tagColors.js';
import { tagGraphCache } from '../tags/tagGraph.js';

// Same glyphs the recipe editor's own toolbar uses (EditToolbar.ts /
// RecipeHeaderStrip.ts).
const ICON_CREATE = '+';
const ICON_MULTI_SELECT = '⛶';
const ICON_EDIT = '✎';
const ICON_DELETE = '×';
const ICON_SAVE = '✓';
const ICON_CANCEL = '✕';
// Circled plus — "combine/add together", and echoes the create button's
// own + glyph inside the same circular shape.
const ICON_MERGE = '⊕';
// Same glyph/rationale as the recipe page's own focus toggle
// (RecipeHeaderStrip.ts) — no plain (non-emoji) magnifying glass exists
// in Unicode, so a crosshair/target glyph stands in for "focus" instead.
const ICON_FOCUS = '⌖';

/** Recipes shown at once in the "on s'utilitza"/"Elaboracions" lists,
 *  before paging kicks in. */
const RECIPES_PER_PAGE = 5;

interface Row {
  entity: EntityData;
  recipeCount: number;
}

/**
 * Ingredient/transformation/tool explorer: a table of every registered
 * entity (with its recipe-usage count). Elaborations (recipe-subgraphs)
 * are an ingredient-only concept — tools skip that section the same way
 * transformations already do. Selecting a row expands it in place — an
 * accordion, not a separate side/below panel — showing the same
 * image/description/recipes-used/elaborations content directly underneath
 * that row, pushing the rows below it down. Only one row is ever expanded
 * at a time (selectedId is both "which row is selected" and "which row is
 * expanded"); clicking the expanded row again collapses it.
 *
 * Browsing is public; create/edit/delete/merge only appear when both
 * authenticated *and* the global edit-mode toggle (site nav) is on — the
 * same flag the recipe page's own edit mode is unified with, via
 * EditModeService. In multi-select mode (picking merge candidates), rows
 * just highlight — no expansion, since the point there is picking, not
 * inspecting. "Create new" is a virtual expanded row prepended above the
 * real ones, holding the same InlineEntityEditor form used for editing.
 */
export class ExplorerPage {
  private container: HTMLElement;
  private entityType: EntityType;
  private rows: Row[] = [];
  private filterText: string = '';
  private selectedId: string | null = null;
  /** Set when isolated on a single row (URL /ingredient/<id> or
   *  /transformation/<id>) — hides the filter box and every other row.
   *  See buildFocusIconButton()/renderTable()/renderRows(). */
  private focusedId: string | null = null;
  private multiSelectMode: boolean = false;
  private selectedIds: Set<string> = new Set();
  private isCreating: boolean = false;
  private isEditingCard: boolean = false;
  private unsubscribeAuth: (() => void) | null = null;
  private unsubscribeEditMode: (() => void) | null = null;

  private tableContainer: HTMLElement | null = null;
  /** The scrollable row list inside tableContainer — kept so refreshRows()
   *  can update it in place without losing scroll position. */
  private rowsListEl: HTMLElement | null = null;
  private toolbarContainer: HTMLElement | null = null;

  constructor(container: HTMLElement, entityType: EntityType, focusId?: string) {
    this.container = container;
    this.entityType = entityType;
    if (focusId) {
      this.selectedId = focusId;
      this.focusedId = focusId;
    }
  }

  async render(): Promise<void> {
    this.container.innerHTML = '';

    const page = document.createElement('div');
    page.className = 'py-4 max-w-full px-4';

    // No page heading here — SiteNav already names this section
    // ("Ingredients"/"Transformacions"/"Utensilis"), so an H1 repeating it would be
    // redundant.
    this.toolbarContainer = document.createElement('div');
    this.toolbarContainer.className = 'mb-3';
    page.appendChild(this.toolbarContainer);

    this.tableContainer = document.createElement('div');
    this.tableContainer.className = 'border border-[var(--accent-alpha-20)] rounded-lg overflow-hidden';
    page.appendChild(this.tableContainer);

    this.container.appendChild(page);

    let loadedOk = true;
    try {
      await this.loadRows();
    } catch (error) {
      loadedOk = false;
      console.error('Failed to load explorer rows:', error);
      showToast('No s\'han pogut carregar els resultats', 'error');
    }

    // Deep-linked to a row that doesn't exist (deleted entity, bad link,
    // typo) — fall back to the plain (unfocused) list rather than
    // silently rendering nothing where the isolated row should be. Only
    // when the load itself actually succeeded — otherwise an empty
    // `rows` just means the fetch failed (already reported above), not
    // that this particular entity doesn't exist.
    if (loadedOk && this.focusedId && !this.rows.some(r => r.entity.id === this.focusedId)) {
      this.focusedId = null;
      this.selectedId = null;
      history.replaceState({}, '', `/${this.entityPluralLabel()}`);
      showToast(this.entityNotFoundMessage(), 'error');
    }

    this.renderToolbar();
    this.renderTable();

    const onEditabilityChange = (): void => {
      if (!this.canEdit()) {
        // Logging out, or leaving edit mode, mid-action falls back to
        // plain browsing.
        this.multiSelectMode = false;
        this.selectedIds.clear();
        this.isEditingCard = false;
        this.isCreating = false;
      }
      this.renderToolbar();
      this.refreshRows();
    };
    this.unsubscribeAuth = authService.subscribe(onEditabilityChange);
    this.unsubscribeEditMode = editModeService.subscribe(onEditabilityChange);
  }

  destroy(): void {
    this.unsubscribeAuth?.();
    this.unsubscribeAuth = null;
    this.unsubscribeEditMode?.();
    this.unsubscribeEditMode = null;
  }

  /** Whether create/edit/delete/merge affordances should be shown —
   *  logged in AND the global edit-mode toggle is on. */
  private canEdit(): boolean {
    return authService.isAuthenticated() && editModeService.isEditMode();
  }

  /** Catalan plural noun per entity type — the list route (`/ingredients`,
   *  `/transformacions`, `/utensilis`), the filter placeholder, and the
   *  merge dialog's copy all just need this one lookup. The *singular*
   *  route (`/${entityType}/<id>`) needs no equivalent — entityType is
   *  already the right English word there, matching the API's own
   *  `/api/ingredient`, `/api/transformation`, `/api/tool` naming. */
  private entityPluralLabel(): string {
    switch (this.entityType) {
      case 'ingredient': return 'ingredients';
      case 'transformation': return 'transformacions';
      case 'tool': return 'utensilis';
    }
  }

  private entityNotFoundMessage(): string {
    switch (this.entityType) {
      case 'ingredient': return 'No s\'ha trobat l\'ingredient';
      case 'transformation': return 'No s\'ha trobat la transformació';
      case 'tool': return 'No s\'ha trobat l\'utensili';
    }
  }

  private async loadRows(): Promise<void> {
    if (this.entityType === 'ingredient') {
      const items = await listIngredients();
      this.rows = items.map(i => ({ entity: i.ingredient, recipeCount: i.recipeCount }));
    } else if (this.entityType === 'transformation') {
      const items = await listTransformations();
      this.rows = items.map(t => ({ entity: t.transformation, recipeCount: t.recipeCount }));
    } else {
      const items = await listTools();
      this.rows = items.map(t => ({ entity: t.tool, recipeCount: t.recipeCount }));
    }
    this.rows.sort((a, b) => a.entity.name.localeCompare(b.entity.name));
  }

  // ============================================================================
  // Toolbar: create / multi-select / merge
  // ============================================================================

  private renderToolbar(): void {
    if (!this.toolbarContainer) return;
    this.toolbarContainer.innerHTML = '';
    // Isolated on a single row (focused) — create/multi-select/merge
    // don't apply to a lone entity, so there's no toolbar at all here.
    if (this.focusedId) return;
    if (!this.canEdit()) return;

    // justify-between: create/merge on the left, multi-select toggle
    // pinned to the right — same circular icon-button look as the recipe
    // editor's own toolbar (EditToolbar.ts's makeButton).
    const row = document.createElement('div');
    row.className = 'flex flex-wrap items-center justify-between gap-2';

    const leftGroup = document.createElement('div');
    leftGroup.className = 'flex flex-wrap items-center gap-2';
    row.appendChild(leftGroup);

    const createBtn = this.buildIconButton(
      ICON_CREATE,
      this.entityType === 'ingredient' ? 'Nou ingredient'
        : this.entityType === 'transformation' ? 'Nova transformació'
        : 'Nou utensili',
      false,
    );
    createBtn.addEventListener('click', () => {
      this.isCreating = true;
      this.isEditingCard = false;
      this.selectedId = null;
      this.multiSelectMode = false;
      this.selectedIds.clear();
      this.renderToolbar();
      this.refreshRows();
    });
    leftGroup.appendChild(createBtn);

    // Edit/delete for whichever single row is selected — mirrors the same
    // pair shown on the row itself (buildRowElement()), so they're reachable
    // from the top bar too without having to scroll to the selected row.
    if (!this.multiSelectMode && !this.isEditingCard && this.selectedId) {
      const selectedRow = this.rows.find(r => r.entity.id === this.selectedId);
      if (selectedRow) {
        leftGroup.appendChild(this.buildEditIconButton());
        leftGroup.appendChild(this.buildDeleteIconButton(selectedRow));
      }
    }

    if (this.multiSelectMode && this.selectedIds.size >= 2) {
      const mergeBtn = this.buildIconButton(ICON_MERGE, `Fusiona (${this.selectedIds.size})`, false);
      mergeBtn.addEventListener('click', () => { void this.handleMergeClick(); });
      leftGroup.appendChild(mergeBtn);
    }

    const multiBtn = this.buildIconButton(ICON_MULTI_SELECT, 'Selecció múltiple', this.multiSelectMode);
    multiBtn.addEventListener('click', () => {
      this.multiSelectMode = !this.multiSelectMode;
      this.selectedIds.clear();
      this.isCreating = false;
      this.isEditingCard = false;
      this.renderToolbar();
      this.refreshRows();
    });
    row.appendChild(multiBtn);

    this.toolbarContainer.appendChild(row);
  }

  private async handleMergeClick(): Promise<void> {
    const candidates = this.rows
      .filter(r => this.selectedIds.has(r.entity.id))
      .map(r => ({ id: r.entity.id, name: r.entity.name }));
    if (candidates.length < 2) return;

    const dialog = new MergeDialog(candidates, this.entityPluralLabel());
    const targetId = await dialog.open();
    if (!targetId) return;

    const sourceIds = candidates.map(c => c.id).filter(id => id !== targetId);
    try {
      if (this.entityType === 'ingredient') {
        await mergeIngredients({ sourceIds, targetId });
      } else if (this.entityType === 'transformation') {
        await mergeTransformations({ sourceIds, targetId });
      } else {
        await mergeTools({ sourceIds, targetId });
      }

      // Reload rather than patch in place — merged references can change
      // the target's own usage count too (nodes that used a source now
      // count toward the target).
      await this.loadRows();
      this.multiSelectMode = false;
      this.selectedIds.clear();
      this.selectedId = targetId;

      this.renderToolbar();
      this.refreshRows();
      showToast('Fusionat correctament', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Error fusionant', 'error');
    }
  }

  // ============================================================================
  // Table
  // ============================================================================

  private renderTable(): void {
    if (!this.tableContainer) return;
    this.tableContainer.innerHTML = '';

    // Isolated on a single row — no filter box, since there's nothing
    // else to filter down to.
    if (!this.focusedId) {
      const filterWrap = document.createElement('div');
      filterWrap.className = 'p-2 border-b border-[var(--accent-alpha-20)]';
      const filterInput = document.createElement('input');
      filterInput.type = 'text';
      filterInput.placeholder = `Filtra ${this.entityPluralLabel()}...`;
      filterInput.value = this.filterText;
      filterInput.className = 'w-full px-3 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
      filterWrap.appendChild(filterInput);
      this.tableContainer.appendChild(filterWrap);

      // Filtering re-renders only the row list, not the input itself, so
      // typing doesn't lose focus/cursor position on every keystroke.
      filterInput.addEventListener('input', () => {
        this.filterText = filterInput.value;
        this.refreshRows();
      });
    }

    this.rowsListEl = document.createElement('div');
    this.rowsListEl.className = 'max-h-[70vh] overflow-y-auto divide-y divide-[var(--accent-alpha-13)]';
    this.tableContainer.appendChild(this.rowsListEl);

    this.refreshRows();
  }

  /**
   * Re-render just the row list in place, preserving its scroll position —
   * unlike renderTable(), which rebuilds the whole table (filter input
   * included) and therefore hands back a fresh, unscrolled row list.
   * Selecting/expanding a row, editing, creating, and merging all only
   * change what's rendered inside the row list, not the table's own
   * structure, so they should never need more than this.
   */
  private refreshRows(): void {
    if (!this.rowsListEl) return;
    const scrollTop = this.rowsListEl.scrollTop;
    this.renderRows(this.rowsListEl);
    this.rowsListEl.scrollTop = scrollTop;
  }

  private matchesFilter(name: string): boolean {
    const q = this.filterText.trim().toLowerCase();
    if (!q) return true;
    return name.toLowerCase().includes(q);
  }

  private renderRows(rowsListEl: HTMLElement): void {
    rowsListEl.innerHTML = '';

    // Isolated on a single row — ignore the filter and every other row;
    // this is the only thing shown.
    if (this.focusedId) {
      const focused = this.rows.find(r => r.entity.id === this.focusedId);
      if (focused) rowsListEl.appendChild(this.buildRowElement(focused));
      return;
    }

    if (this.isCreating) {
      rowsListEl.appendChild(this.buildCreateRowElement());
    }

    const filtered = this.rows.filter(r => this.matchesFilter(r.entity.name));

    if (filtered.length === 0) {
      if (!this.isCreating) {
        const empty = document.createElement('div');
        empty.className = 'p-4 text-sm text-[#888] text-center';
        empty.textContent = 'Cap resultat';
        rowsListEl.appendChild(empty);
      }
      return;
    }

    for (const row of filtered) {
      rowsListEl.appendChild(this.buildRowElement(row));
    }
  }

  /**
   * One row: a clickable summary line, plus — if this is the single
   * expanded row (never true in multi-select mode, where rows only
   * highlight) — the same view/edit content that used to live in a
   * separate side panel, mounted directly underneath it.
   */
  private buildRowElement(row: Row): HTMLElement {
    const { entity } = row;
    const isMultiSelected = this.multiSelectMode && this.selectedIds.has(entity.id);
    const isExpanded = !this.multiSelectMode && this.selectedId === entity.id;

    const wrapper = document.createElement('div');

    const summary = document.createElement('div');
    summary.className = 'px-3 py-2 flex items-center gap-3 cursor-pointer transition-colors border-l-2 ' +
      (isMultiSelected || isExpanded
        ? 'bg-[var(--accent-alpha-13)] border-[var(--accent)]'
        : 'hover:bg-[var(--accent-alpha-08)] border-transparent');

    // The miniature image + name are omitted once expanded — the content
    // below (mountViewContent) already opens with its own, full-size
    // image + untruncated name "title", so showing both here would be a
    // duplicate (and on narrow screens, a long name never fit in the row
    // anyway — this is where it actually becomes fully visible).
    const rowTags = !isExpanded && this.entityType === 'ingredient' ? ((entity as Ingredient).tags ?? []) : [];

    if (!isExpanded) {
      summary.appendChild(this.buildMiniImage(entity));

      const nameSpan = document.createElement('span');
      nameSpan.textContent = entity.name;
      if (rowTags.length > 0) {
        // Collapsed ingredient rows with tags get a narrower, content-sized
        // name (capped at half the row so a long name can't starve it
        // entirely) plus a tags area that takes whatever's left — fitted to
        // however many chips actually fit (see fitRowTags()). Every other
        // case (transformations, tag-less ingredients, or the row currently
        // expanded) keeps the plain flex-1 name unchanged — no tags area at
        // all, so there's no behavior change for those.
        nameSpan.className = 'flex-shrink-0 max-w-[50%] text-sm text-[#e0e0e0] truncate';
      } else {
        nameSpan.className = 'flex-1 text-sm text-[#e0e0e0] truncate';
      }
      summary.appendChild(nameSpan);
    } else {
      // Keeps the row's height/spacing consistent and pushes the focus/
      // edit/delete actions below to the right, same as the image+name
      // otherwise would.
      const spacer = document.createElement('div');
      spacer.className = 'flex-1';
      summary.appendChild(spacer);
    }

    if (rowTags.length > 0) {
      const tagsContainer = document.createElement('div');
      tagsContainer.className = 'flex-1 min-w-0 flex items-center gap-1 overflow-hidden';
      summary.appendChild(tagsContainer);
      // Deferred to the next frame: the row (and this container) must
      // already be laid out in the live DOM for clientWidth/offsetWidth
      // measurements below to mean anything — buildRowElement() only
      // returns the wrapper here, the caller appends it synchronously
      // right after, so by the next frame it's in place.
      const explicitCandidates = rowTags.map(t => ({ tag: t, implied: false }));
      requestAnimationFrame(() => this.fitRowTags(tagsContainer, explicitCandidates));

      // Implied tags (via the tag-implication graph) are candidates for
      // the same fit too, appended after the explicit ones — so they're
      // the first to get cut when space runs out. The graph cache is
      // usually not loaded by the very next frame (a real fetch, unlike
      // the layout-only wait above), so this re-fits from scratch once it
      // resolves, same "explicit now, implied once ready" pattern used
      // elsewhere for tag chips.
      tagGraphCache.ensureLoaded().then(() => {
        if (!tagsContainer.isConnected) return;
        const implied = tagGraphCache.getImpliedTags(rowTags).map(t => ({ tag: t, implied: true }));
        if (implied.length === 0) return;
        this.fitRowTags(tagsContainer, [...explicitCandidates, ...implied]);
      }).catch((error) => {
        console.error('Failed to load tag implications:', error);
      });
    }

    // Focus (isolate this row, /ingredient/<id> or /transformation/<id>)
    // plus edit/delete for the expanded row, at the right — the edit/
    // delete pair is also reachable from the top bar (renderToolbar())
    // while this row is selected; focus isn't, since the toolbar only
    // ever renders for editors (see renderToolbar()) and focus is
    // available regardless of login state. While actively editing, this
    // same spot instead holds save/cancel (✓/✕) for the form below — see
    // editActionsContainer.
    let editActionsContainer: HTMLElement | null = null;
    if (isExpanded) {
      const actions = document.createElement('div');
      actions.className = 'flex items-center gap-1 flex-shrink-0';
      actions.appendChild(this.buildFocusIconButton(entity.id));

      if (this.canEdit()) {
        if (this.isEditingCard) {
          editActionsContainer = document.createElement('div');
          editActionsContainer.className = 'flex items-center gap-1';
          actions.appendChild(editActionsContainer);
        } else {
          actions.appendChild(this.buildEditIconButton());
          actions.appendChild(this.buildDeleteIconButton(row));
        }
      }

      summary.appendChild(actions);
    }

    summary.addEventListener('click', () => {
      // Isolated on this row — it's always the expanded one while
      // focused (there's nothing else to show if collapsed), so a plain
      // click on the summary is a no-op; buildFocusIconButton() is the
      // only way out.
      if (this.focusedId) return;
      if (this.multiSelectMode) {
        if (this.selectedIds.has(entity.id)) {
          this.selectedIds.delete(entity.id);
        } else {
          this.selectedIds.add(entity.id);
        }
        this.renderToolbar();
        this.refreshRows();
        return;
      }
      this.isCreating = false;
      if (this.selectedId === entity.id) {
        // Clicking the already-expanded row again collapses it.
        this.selectedId = null;
        this.isEditingCard = false;
      } else {
        this.selectedId = entity.id;
        this.isEditingCard = false;
      }
      // selectedId changing affects whether the toolbar's own edit/delete
      // icons show (renderToolbar()'s !!this.selectedId check) — without
      // this they only appeared to sync on some unrelated re-render (e.g.
      // toggling edit mode), not on the selection change that actually
      // caused it.
      this.renderToolbar();
      this.refreshRows();
    });

    wrapper.appendChild(summary);

    if (isExpanded) {
      const content = document.createElement('div');
      content.className = 'px-4 pt-1 pb-4 border-l-2 border-[var(--accent)] bg-[var(--accent-alpha-08)]';
      if (this.isEditingCard && editActionsContainer) {
        this.mountEditForm(content, row, editActionsContainer);
      } else {
        this.mountViewContent(content, row);
      }
      wrapper.appendChild(content);
    }

    return wrapper;
  }

  /**
   * Fit as many tag chips as fit into `container`'s available width —
   * whole chips only, never a clipped partial one. Adds an ellipsis chip
   * when some are left over; shows nothing at all if not even one chip
   * fits. Called via requestAnimationFrame from buildRowElement() once
   * the row is actually laid out in the live DOM (clientWidth/offsetWidth
   * are meaningless before that).
   */
  private fitRowTags(container: HTMLElement, candidates: { tag: Tag; implied: boolean }[]): void {
    // The row may have been replaced by a re-render (filter typing,
    // selection change) before this frame ran — nothing to fit into.
    if (!container.isConnected) return;
    container.innerHTML = '';

    const chips = candidates.map(c => this.buildTagChip(c.tag.name, c.tag.color, c.implied));
    for (const chip of chips) container.appendChild(chip);

    const ellipsisChip = this.buildTagChip('…');
    container.appendChild(ellipsisChip);
    const ellipsisWidth = ellipsisChip.offsetWidth;
    container.removeChild(ellipsisChip);

    const GAP = 4; // matches the container's own gap-1 (0.25rem)
    const maxWidth = container.clientWidth;
    let usedWidth = 0;
    let visibleCount = 0;
    for (let i = 0; i < chips.length; i++) {
      const isLast = i === chips.length - 1;
      const reserve = isLast ? 0 : ellipsisWidth + GAP;
      const withThisChip = usedWidth + (visibleCount > 0 ? GAP : 0) + chips[i].offsetWidth;
      if (withThisChip + reserve <= maxWidth) {
        usedWidth = withThisChip;
        visibleCount++;
      } else {
        break;
      }
    }

    for (let i = chips.length - 1; i >= visibleCount; i--) {
      container.removeChild(chips[i]);
    }
    if (visibleCount < chips.length) {
      container.appendChild(ellipsisChip);
    }
    // visibleCount === 0 leaves the container empty — no chip, no
    // ellipsis — per "show nothing when there's no space at all".
  }

  /** Tag chip for the expanded read-only view (mountViewContent) — a
   *  full Tag object rather than fitRowTags'/buildTagChip's plain text,
   *  since there's no width-fitting constraint here to have already
   *  reduced it to just name+color. */
  private buildViewTagChip(tag: Tag, implied: boolean): HTMLElement {
    const chip = document.createElement('span');
    chip.className = 'text-xs px-2 py-0.5 rounded-full border font-mono';
    chip.style.cssText = tagChipStyle(tag.color);
    if (implied) {
      chip.style.opacity = '0.55';
      chip.title = 'Implícita — no assignada directament';
    }
    chip.textContent = tag.name;
    return chip;
  }

  private buildTagChip(text: string, colorKey?: string, implied: boolean = false): HTMLElement {
    const chip = document.createElement('span');
    chip.className = 'flex-shrink-0 text-xs px-2 py-0.5 rounded-full border font-mono whitespace-nowrap';
    chip.style.cssText = tagChipStyle(colorKey);
    if (implied) {
      chip.style.opacity = '0.55';
      chip.title = 'Implícita — no assignada directament';
    }
    chip.textContent = text;
    return chip;
  }

  /** The virtual expanded row prepended above the real ones while creating. */
  private buildCreateRowElement(): HTMLElement {
    const wrapper = document.createElement('div');
    const content = document.createElement('div');
    content.className = 'px-4 py-3 border-l-2 border-[var(--accent)] bg-[var(--accent-alpha-08)]';
    wrapper.appendChild(content);

    const editor = new InlineEntityEditor({
      entityType: this.entityType,
      container: content,
      onCreated: (entity) => {
        this.rows.push({ entity, recipeCount: 0 });
        this.rows.sort((a, b) => a.entity.name.localeCompare(b.entity.name));
        this.isCreating = false;
        this.selectedId = entity.id;
        this.renderToolbar();
        this.refreshRows();
        showToast(
          this.entityType === 'ingredient' ? 'Ingredient creat'
            : this.entityType === 'transformation' ? 'Transformació creada'
            : 'Utensili creat',
          'success',
        );
      },
      onCancelled: () => {
        this.isCreating = false;
        this.refreshRows();
      },
    });
    editor.render();

    return wrapper;
  }

  private mountEditForm(container: HTMLElement, row: Row, actionsContainer: HTMLElement): void {
    const editor = new InlineEntityEditor({
      entityType: this.entityType,
      existingEntity: row.entity,
      container,
      onUpdated: (entity) => {
        row.entity = entity;
        this.isEditingCard = false;
        this.renderToolbar();
        this.refreshRows();
        showToast('Desat', 'success');
      },
      onCancelled: () => {
        this.isEditingCard = false;
        this.renderToolbar();
        this.refreshRows();
      },
      onImagesChanged: (entity) => {
        row.entity = entity;
      },
      // Save/cancel are driven from ✓/✕ icon buttons placed in the row's
      // own summary line (left of the count) instead of the form's usual
      // button row below it — same spot the edit/delete icons occupy in
      // view mode.
      onActionsReady: ({ save, cancel }) => {
        const saveBtn = this.buildIconButton(ICON_SAVE, 'Desa', true);
        saveBtn.addEventListener('click', (e) => { e.stopPropagation(); save(); });
        const cancelBtn = this.buildIconButton(ICON_CANCEL, 'Cancel·la', false);
        cancelBtn.addEventListener('click', (e) => { e.stopPropagation(); cancel(); });
        actionsContainer.appendChild(saveBtn);
        actionsContainer.appendChild(cancelBtn);
      },
    });
    editor.render();
  }

  private mountViewContent(container: HTMLElement, row: Row): void {
    const { entity } = row;

    const wrapper = document.createElement('div');
    wrapper.className = 'space-y-3 pt-2';

    // Title — the name the collapsed row shows (truncated there), moved
    // here (the row omits it while expanded, see buildRowElement()) so a
    // long name that a narrow screen could never fit in the row is
    // finally shown in full, never truncated. No image here — the
    // image cycler right below already shows one, bigger.
    const header = document.createElement('div');
    header.className = 'flex items-center justify-center gap-2';
    const nameEl = document.createElement('span');
    nameEl.className = 'text-sm text-[#e0e0e0] font-mono text-center';
    nameEl.textContent = entity.name;
    header.appendChild(nameEl);
    wrapper.appendChild(header);

    wrapper.appendChild(this.buildImageCycler(entity.images ?? []));

    if (entity.description) {
      const desc = document.createElement('p');
      desc.className = 'text-sm text-[#a0a0a0]';
      desc.textContent = entity.description;
      wrapper.appendChild(desc);
    }

    if (this.entityType === 'ingredient') {
      const tags = (entity as Ingredient).tags ?? [];
      if (tags.length > 0) {
        const tagsRow = document.createElement('div');
        tagsRow.className = 'flex flex-wrap gap-1.5 justify-center';
        for (const tag of tags) {
          tagsRow.appendChild(this.buildViewTagChip(tag, false));
        }
        wrapper.appendChild(tagsRow);

        // Implied tags (via the tag-implication graph) render alongside
        // the explicit ones above, dimmed — patched in once the graph
        // cache resolves, guarded against a since-collapsed/replaced row.
        tagGraphCache.ensureLoaded().then(() => {
          if (!tagsRow.isConnected) return;
          for (const implied of tagGraphCache.getImpliedTags(tags)) {
            tagsRow.appendChild(this.buildViewTagChip(implied, true));
          }
        }).catch((error) => {
          console.error('Failed to load tag implications:', error);
        });
      }
    }

    const usingSection = this.buildRecipeSection('Receptes on s\'utilitza');
    wrapper.appendChild(usingSection.section);

    const elabSection = this.entityType === 'ingredient' ? this.buildElaborationSection() : null;
    if (elabSection) wrapper.appendChild(elabSection.section);

    container.appendChild(wrapper);

    // Guard against a stale response landing after the user has already
    // selected/collapsed something else — only apply it if still current.
    const entityId = entity.id;
    (this.entityType === 'ingredient'
      ? getRecipesUsingIngredient(entityId)
      : this.entityType === 'transformation'
      ? getRecipesUsingTransformation(entityId)
      : getRecipesUsingTool(entityId)
    ).then(recipes => {
      if (this.selectedId !== entityId) return;
      this.renderPaginatedRecipeList(usingSection, 'Receptes on s\'utilitza', recipes);
    }).catch(() => {
      if (this.selectedId !== entityId) return;
      usingSection.list.textContent = 'Error carregant receptes';
    });

    if (this.entityType === 'ingredient' && elabSection) {
      // Quantities in the elaboration view need unit display names
      // (unitCache) — unlike the recipe page, nothing else on this route
      // preloads it, so wait for it alongside the subgraphs themselves;
      // otherwise quantities would render with the raw unit id (e.g.
      // "1 builtin_units") until something else happened to trigger a load.
      Promise.all([getIngredientRecipeSubgraphs(entityId), unitCache.ensureLoaded()]).then(([subgraphs]) => {
        if (this.selectedId !== entityId) return;
        this.renderElaborationSubgraphs(elabSection, subgraphs);
      }).catch(() => {
        if (this.selectedId !== entityId) return;
        elabSection.body.textContent = 'Error carregant elaboracions';
      });
    }
  }

  /** The small circular image (or placeholder) used both in the
   *  collapsed row and, full-size text alongside it, as the expanded
   *  view's own title header — see mountViewContent(). */
  private buildMiniImage(entity: EntityData): HTMLElement {
    const imageUrl = getDefaultImageUrl(entity.images);
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = entity.name;
      img.className = 'rounded-full flex-shrink-0';
      img.style.cssText = 'width: 28px; height: 28px; object-fit: cover;';
      return img;
    }
    const placeholder = document.createElement('div');
    placeholder.className = 'rounded-full bg-[#1a1a2e] border border-[var(--accent-alpha-20)] flex-shrink-0';
    placeholder.style.cssText = 'width: 28px; height: 28px;';
    return placeholder;
  }

  private buildSectionLabel(text: string): HTMLElement {
    const label = document.createElement('h3');
    label.className = 'text-xs font-mono uppercase tracking-wider text-[#a0a0a0]';
    label.textContent = text;
    return label;
  }

  private buildRecipeSection(labelText: string): { section: HTMLElement; label: HTMLElement; list: HTMLElement } {
    const section = document.createElement('div');
    section.className = 'space-y-1';

    const label = this.buildSectionLabel(labelText);
    section.appendChild(label);

    const list = document.createElement('div');
    list.className = 'space-y-1';
    list.textContent = 'Carregant...';
    section.appendChild(list);

    return { section, label, list };
  }

  /**
   * Fills in a section's count ("Receptes on s'utilitza (N)"), and shows
   * its recipes RECIPES_PER_PAGE at a time with ‹/› paging (same cycling
   * widget as buildImageCycler, reused via buildCyclerArrow) once there
   * are more than that. N is the actual length of what's being shown,
   * not the row's own recipeCount badge — the two are computed by
   * different queries (usage-count vs. graph reachability) that usually
   * but aren't guaranteed to agree, and this way the title can never
   * disagree with what's actually listed below it.
   */
  private renderPaginatedRecipeList(
    { label, list }: { label: HTMLElement; list: HTMLElement },
    labelPrefix: string,
    recipes: RecipeListItem[],
  ): void {
    label.textContent = `${labelPrefix} (${recipes.length})`;

    if (recipes.length === 0) {
      list.innerHTML = '';
      const empty = document.createElement('div');
      empty.className = 'text-xs text-[#666]';
      empty.textContent = 'Cap';
      list.appendChild(empty);
      return;
    }

    const totalPages = Math.ceil(recipes.length / RECIPES_PER_PAGE);
    let page = 0;

    const renderPage = (): void => {
      list.innerHTML = '';
      const start = page * RECIPES_PER_PAGE;
      for (const recipe of recipes.slice(start, start + RECIPES_PER_PAGE)) {
        list.appendChild(this.buildRecipeLink(recipe));
      }

      if (totalPages > 1) {
        const pager = document.createElement('div');
        pager.className = 'flex items-center justify-center gap-3 pt-1';
        pager.appendChild(this.buildCyclerArrow('‹', () => {
          page = (page - 1 + totalPages) % totalPages;
          renderPage();
        }));
        const counter = document.createElement('span');
        counter.className = 'text-xs text-[#888] font-mono';
        counter.textContent = `${page + 1} / ${totalPages}`;
        pager.appendChild(counter);
        pager.appendChild(this.buildCyclerArrow('›', () => {
          page = (page + 1) % totalPages;
          renderPage();
        }));
        list.appendChild(pager);
      }
    };

    renderPage();
  }

  private buildRecipeLink(recipe: RecipeListItem): HTMLElement {
    const a = document.createElement('a');
    a.href = `/recipe/${recipe.id}`;
    a.className = 'flex items-center gap-2 px-2 py-1 -mx-2 rounded-md hover:bg-[var(--accent-alpha-08)] text-sm text-[#e0e0e0]';
    if (recipe.imageUrl) {
      const img = document.createElement('img');
      img.src = recipe.imageUrl;
      img.alt = '';
      img.className = 'rounded-full flex-shrink-0';
      img.style.cssText = 'width: 20px; height: 20px; object-fit: cover;';
      a.appendChild(img);
    }
    const span = document.createElement('span');
    span.className = 'truncate';
    span.textContent = recipe.title;
    a.appendChild(span);
    return a;
  }

  /**
   * "Elaboracions" section shell — filled in by renderElaborationSubgraphs
   * once the subgraphs have loaded. Separate from buildRecipeSection
   * (which the "Receptes on s'utilitza" list above still uses) since this
   * one holds a single rendered elaboration view rather than a list of
   * recipe links.
   */
  private buildElaborationSection(): { section: HTMLElement; label: HTMLElement; body: HTMLElement } {
    const section = document.createElement('div');
    section.className = 'space-y-1';

    const label = this.buildSectionLabel('Elaboracions');
    section.appendChild(label);

    const body = document.createElement('div');
    body.className = 'space-y-1';
    body.textContent = 'Carregant...';
    section.appendChild(body);

    return { section, label, body };
  }

  /**
   * Fills in the "Elaboracions (N)" count, then shows exactly one
   * subgraph at a time as an ingredients + preparation view — narrow
   * screens stacked, wide screens side by side, same layout as the recipe
   * page's own Elaboració view — with ‹/› paging (buildCyclerArrow, same
   * widget as the image cycler and the recipe list above) once there's
   * more than one.
   */
  private renderElaborationSubgraphs(
    { label, body }: { label: HTMLElement; body: HTMLElement },
    subgraphs: IngredientRecipeSubgraphResult[],
  ): void {
    label.textContent = `Elaboracions (${subgraphs.length})`;

    if (subgraphs.length === 0) {
      body.innerHTML = '';
      const empty = document.createElement('div');
      empty.className = 'text-xs text-[#666]';
      empty.textContent = 'Cap';
      body.appendChild(empty);
      return;
    }

    let index = 0;

    const renderCurrent = (): void => {
      body.innerHTML = '';
      const sg = subgraphs[index];

      // Framed border, same treatment as the recipe page's own Elaboració
      // panel (HomePage.ts's PANEL_CLASSES) — sets this off visually from
      // the plain recipe-link list above it. Which recipe this came from
      // is a footnote below the actual content, not a header — see
      // buildSubgraphFooterHtml()'s own doc comment.
      const view = document.createElement('div');
      view.className = 'border border-[var(--accent-alpha-20)] rounded-lg bg-[#1a1a2e] overflow-hidden';

      const content = document.createElement('div');
      content.className = 'flex flex-col lg:flex-row gap-4 p-4';
      content.appendChild(this.buildElaborationColumn('Ingredients', buildSubgraphIngredientsHtml(sg.nodes)));
      content.appendChild(this.buildElaborationColumn('Preparació', buildSubgraphPreparationHtml(sg.nodes)));
      view.appendChild(content);

      const footer = document.createElement('div');
      footer.className = 'px-4 py-2 border-t border-[var(--accent-alpha-20)]';
      footer.innerHTML = buildSubgraphFooterHtml(sg);
      view.appendChild(footer);

      body.appendChild(view);

      if (subgraphs.length > 1) {
        const pager = document.createElement('div');
        pager.className = 'flex items-center justify-center gap-3 pt-1';
        pager.appendChild(this.buildCyclerArrow('‹', () => {
          index = (index - 1 + subgraphs.length) % subgraphs.length;
          renderCurrent();
        }));
        const counter = document.createElement('span');
        counter.className = 'text-xs text-[#888] font-mono';
        counter.textContent = `${index + 1} / ${subgraphs.length}`;
        pager.appendChild(counter);
        pager.appendChild(this.buildCyclerArrow('›', () => {
          index = (index + 1) % subgraphs.length;
          renderCurrent();
        }));
        body.appendChild(pager);
      }
    };

    renderCurrent();
  }

  private buildElaborationColumn(heading: string, innerHtml: string): HTMLElement {
    const col = document.createElement('div');
    col.className = 'flex-1 min-w-0 space-y-1';

    const label = document.createElement('div');
    label.className = 'text-xs font-mono uppercase tracking-wider text-[#888]';
    label.textContent = heading;
    col.appendChild(label);

    const body = document.createElement('div');
    body.innerHTML = innerHtml;
    col.appendChild(body);

    return col;
  }

  /**
   * Read-only prev/next image cycler for the expanded row's view content.
   * Deliberately not ImagePool: that component is built around either
   * picking for a node or managing a pool (add/delete/set-default always
   * available in manage mode), neither of which fits "just browse, no
   * side effects" — so this is a small dedicated widget instead of
   * contorting ImagePool's modes.
   */
  private buildImageCycler(images: Image[]): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col items-center gap-1';

    let index = 0;
    const row = document.createElement('div');
    row.className = 'flex items-center justify-center gap-3';
    wrapper.appendChild(row);

    const counter = document.createElement('div');
    counter.className = 'text-xs text-[#888] font-mono';
    wrapper.appendChild(counter);

    const renderCurrent = (): void => {
      row.innerHTML = '';
      if (images.length === 0) {
        const placeholder = document.createElement('div');
        placeholder.className = 'w-24 h-24 rounded-full border-2 border-dashed border-[#333] flex items-center justify-center text-xs text-[#666]';
        placeholder.textContent = 'Sense imatge';
        row.appendChild(placeholder);
        counter.textContent = '';
        return;
      }

      if (images.length > 1) {
        row.appendChild(this.buildCyclerArrow('‹', () => {
          index = (index - 1 + images.length) % images.length;
          renderCurrent();
        }));
      }

      const img = document.createElement('img');
      img.src = images[index].url;
      img.alt = '';
      img.className = 'w-24 h-24 rounded-full object-cover border-2 border-[var(--accent-alpha-20)]';
      row.appendChild(img);

      if (images.length > 1) {
        row.appendChild(this.buildCyclerArrow('›', () => {
          index = (index + 1) % images.length;
          renderCurrent();
        }));
      }

      counter.textContent = images.length > 1 ? `${index + 1} / ${images.length}` : '';
    };

    renderCurrent();
    return wrapper;
  }

  /**
   * Circular icon button matching the recipe editor's own toolbar look
   * (EditToolbar.ts's makeButton / RecipeHeaderStrip.ts's makeIconButton)
   * — used for the toolbar's create and multi-select-toggle buttons, so
   * the explorer's edit affordances read as the same visual language as
   * recipe editing rather than a separate style.
   */
  private buildIconButton(icon: string, title: string, active: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.title = title;
    btn.textContent = icon;
    btn.style.cssText = `
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: bold;
      font-family: inherit;
      border-radius: 50%;
      border: 1px solid ${active ? 'var(--accent)' : 'var(--accent-alpha-40)'};
      background: ${active ? 'var(--accent)' : 'transparent'};
      color: ${active ? '#0d1117' : 'var(--accent)'};
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    return btn;
  }

  private buildCyclerArrow(glyph: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = glyph;
    btn.className = 'w-7 h-7 flex items-center justify-center rounded-full border border-[var(--accent-alpha-40)] text-[var(--accent)] hover:bg-[var(--accent-alpha-13)] font-mono';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  /**
   * Isolate this row — /ingredient/<id> or /transformation/<id>, hiding
   * the filter box and every other row (see renderTable()/renderRows()).
   * A plain toggle, not arm/confirm: filled while this row is the focused
   * one, exactly like the recipe page's own focus button
   * (RecipeHeaderStrip.ts) — clicking it again unfocuses. Shown
   * regardless of login state, unlike edit/delete: it's a navigation
   * affordance, not an edit one.
   */
  private buildFocusIconButton(entityId: string): HTMLButtonElement {
    const isFocused = this.focusedId === entityId;
    const btn = this.buildIconButton(ICON_FOCUS, isFocused ? 'Torna a la llista' : 'Centra', isFocused);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const listPath = `/${this.entityPluralLabel()}`;
      if (this.focusedId === entityId) {
        this.focusedId = null;
        history.pushState({}, '', listPath);
      } else {
        this.focusedId = entityId;
        this.selectedId = entityId;
        history.pushState({}, '', `/${this.entityType}/${entityId}`);
      }
      // A structural change (the filter box appears/disappears), not just
      // a row refresh.
      this.renderToolbar();
      this.renderTable();
    });
    return btn;
  }

  private buildEditIconButton(): HTMLButtonElement {
    const btn = this.buildIconButton(ICON_EDIT, 'Edita', false);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.isEditingCard = true;
      this.renderToolbar();
      this.refreshRows();
    });
    return btn;
  }

  /**
   * Two-click arm/confirm delete — same mechanism as everywhere else
   * (ImageUploader, ImagePool, EditToolbar's own delete-all button) and
   * now the same *style* too: armed inverts to a filled accent circle,
   * exactly like EditToolbar.armDeleteAll()/disarmDeleteAll(), rather than
   * the red text-button treatment this used to have.
   */
  private buildDeleteIconButton(row: Row): HTMLButtonElement {
    const idleTitle = row.recipeCount > 0 ? `Esborra (usat en ${row.recipeCount})` : 'Esborra';
    const btn = this.buildIconButton(ICON_DELETE, idleTitle, false);

    let armed = false;
    let outsideHandler: ((e: MouseEvent) => void) | null = null;
    const disarm = (): void => {
      if (outsideHandler) {
        document.removeEventListener('click', outsideHandler);
        outsideHandler = null;
      }
      if (!armed) return;
      armed = false;
      btn.style.background = 'transparent';
      btn.style.color = 'var(--accent)';
      btn.style.borderColor = 'var(--accent-alpha-40)';
      btn.title = idleTitle;
    };

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!armed) {
        armed = true;
        btn.style.background = 'var(--accent)';
        btn.style.color = '#0d1117';
        btn.style.borderColor = 'var(--accent)';
        btn.title = 'Confirma l\'esborrat';
        outsideHandler = (ev: MouseEvent) => {
          if (ev.target !== btn) disarm();
        };
        document.addEventListener('click', outsideHandler);
        return;
      }
      disarm();
      void this.confirmDelete(row);
    });

    return btn;
  }

  private async confirmDelete(row: Row): Promise<void> {
    try {
      if (this.entityType === 'ingredient') {
        await deleteIngredient(row.entity.id);
      } else if (this.entityType === 'transformation') {
        await deleteTransformation(row.entity.id);
      } else {
        await deleteTool(row.entity.id);
      }
      this.rows = this.rows.filter(r => r.entity.id !== row.entity.id);
      this.selectedId = null;
      this.renderToolbar();
      this.refreshRows();
      showToast('Esborrat', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Error esborrant', 'error');
    }
  }
}
